/* ======================================================
   CONFIG
====================================================== */


/* ======================================================
   CUSTOM ELEMENT
====================================================== */
class DoctorConsultationForm extends HTMLElement {
  constructor() {
    super();

    this.openButton = null;
    this.popup = null;
    this.popupWrapper = null;
    this.closeButton = null;
    this.form = null;
    this.successMsg = null;
    this.submitBtn = null;

    this.selectedCity = "";
    this.selectedClinic = "";
    this.clinicData = {};
    this.GOOGLE_SHEET_URL = this.dataset.doctorConsultationForm

    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEsc = this.handleEsc.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    this.openButton = this.querySelector(".doctor-consultation-form__form-open-button");
    this.popup = this.querySelector(".doctor-consultation-form__popup-form");
    this.popupWrapper = this.querySelector(
      ".doctor-consultation-form__popup-form-wrapper",
    );
    this.closeButton = this.querySelector(
      ".doctor-consultation-form__popup-close-btn",
    );
    this.form = this.querySelector(".doctor-consultation-form__main-form");
    this.submitBtn = this.querySelector(
      ".doctor-consultation-form__submit-btn",
    );

    if (this.openButton)
      this.openButton.addEventListener("click", this.handleOpen);

    if (this.closeButton)
      this.closeButton.addEventListener("click", this.handleClose);

    if (this.popup)
      this.popup.addEventListener("click", this.handleOutsideClick);

    if (this.form) this.form.addEventListener("submit", this.handleSubmit);

    this.setupInputRestrictions();
    this.loadClinicData();
    this.setupCityClinicHandlers();
    this.injectSuccessMessage();

    document.addEventListener("keydown", this.handleEsc);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleEsc);
  }

  /* ======================================================
     POPUP OPEN / CLOSE
  ====================================================== */
  handleOpen() {
    this.popup.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  handleClose() {
    this.closePopup();
  }

  handleOutsideClick(e) {
    if (!this.popupWrapper.contains(e.target)) {
      this.closePopup();
    }
  }

  handleEsc(e) {
    if (e.key === "Escape") {
      this.closePopup();
    }
  }

  closePopup() {
    this.popup.classList.remove("active");
    document.body.style.overflow = "";
  }

  /* ======================================================
     INPUT HARD RESTRICTIONS
  ====================================================== */
  setupInputRestrictions() {
    const nameInput = this.querySelector("#fullName");
    const phoneInput = this.querySelector("#phoneNumber");

    if (nameInput) {
      nameInput.addEventListener("input", () => {
        nameInput.value = nameInput.value.replace(/[^A-Za-z ]/g, "");
      });
    }

    if (phoneInput) {
      phoneInput.addEventListener("input", () => {
        let v = phoneInput.value.replace(/[^0-9]/g, "");

        if (v.length > 0 && !/^[6-9]/.test(v)) v = "";
        if (v.length > 10) v = v.slice(0, 10);

        phoneInput.value = v;
      });
    }
  }

  /* ======================================================
     LOAD CLINIC DATA
  ====================================================== */
  loadClinicData() {
    // Updated fallback data with new city keys
    this.clinicData = {
      "Bengaluru, Karnataka": ["Indiranagar, 12th Main Road", "Koramangala, 6th Block", "HSR Layout", "Jayanagar, 4th Block", "Whitefield", "Sarjapur", "Shivaji Nagar"],
      "Chennai, Tamil Nadu": ["Anna Nagar", "Poes Garden"],
      "Delhi": ["Green Park"],
      "Mumbai, Maharashtra": ["Marol", "Kalyan"],
      "Pune, Maharashtra": ["Hadapsar"],
      "Hyderabad, Telangana": ["Somajiguda"],
      "Vizag, AP": ["Venkojipalem"],
      "Ernakulam, Kerala": ["Alangad", "Edapally", "Pallimukku"],
      "Thiruvananthapuram, Kerala": ["Kowdiar"],
      "Kochi, Kerala": ["Aluva"]
    };

    const clinicDataAttr = this.getAttribute("data-clinic-data");
    console.log("Raw clinic data attribute:", clinicDataAttr);
    
    if (clinicDataAttr && clinicDataAttr.trim()) {
      try {
        const parsed = JSON.parse(clinicDataAttr);
        this.clinicData = parsed;
        console.log("Successfully loaded clinic data from attribute:", this.clinicData);
      } catch (e) {
        console.error("Failed to parse clinic data, using fallback:", e);
      }
    } else {
      console.log("No clinic data attribute found, using fallback data");
    }
    
    console.log("Final clinic data:", this.clinicData);
  }

  /* ======================================================
     CITY & CLINIC HANDLERS
  ====================================================== */
  setupCityClinicHandlers() {
    const citySelect = this.querySelector("#preferredCity");
    const clinicSelect = this.querySelector("#preferredClinic");

    if (!citySelect || !clinicSelect) return;

    // Get the first city from the dropdown options (not hardcoded)
    const firstCityOption = citySelect.querySelector('option[value]:not([value=""])');
    const defaultCity = firstCityOption ? firstCityOption.value : "";

    if (defaultCity) {
      citySelect.value = defaultCity;
      this.selectedCity = defaultCity;
      console.log("Set default city to:", defaultCity);
      
      // Initialize clinic options with default city's clinics
      this.updateClinicOptions(clinicSelect);
    }

    // City change handler
    citySelect.addEventListener("change", (e) => {
      this.selectedCity = e.target.value;
      console.log("City changed to:", this.selectedCity);
      this.updateClinicOptions(clinicSelect);
    });

    // Clinic change handler
    clinicSelect.addEventListener("change", (e) => {
      this.selectedClinic = e.target.value;
      console.log("Clinic changed to:", this.selectedClinic);
    });
  }

  updateClinicOptions(clinicSelect) {
    // Clear existing options
    clinicSelect.innerHTML = "";

    if (!this.selectedCity) {
      // No city selected
      clinicSelect.disabled = false;
      const option = document.createElement("option");
      option.value = "";
      option.textContent = "Select a clinic (Optional)";
      clinicSelect.appendChild(option);
      return;
    }

    // Get clinics for selected city
    const clinics = this.clinicData[this.selectedCity] || [];
    console.log("Clinics for", this.selectedCity, ":", clinics);

    if (clinics.length === 0) {
      clinicSelect.disabled = false;
      const option = document.createElement("option");
      option.value = "";
      option.textContent = "No clinics available";
      clinicSelect.appendChild(option);
      return;
    }

    // Enable and populate clinic dropdown
    clinicSelect.disabled = false;

    // Add clinic options (no placeholder, directly add clinics)
    clinics.forEach((clinic, index) => {
      const option = document.createElement("option");
      option.value = clinic;
      option.textContent = clinic;
      clinicSelect.appendChild(option);
    });

    // Auto-select first clinic
    if (clinics.length > 0) {
      clinicSelect.value = clinics[0];
      this.selectedClinic = clinics[0];
      console.log("Auto-selected first clinic:", this.selectedClinic);
    }
  }

  /* ======================================================
     SUCCESS MESSAGE
  ====================================================== */
  injectSuccessMessage() {
    this.successMsg = document.createElement("div");
    this.successMsg.className = "doctor-consultation-form__success";
    this.successMsg.textContent =
      "We have received your request and will contact you soon.";
    this.successMsg.style.display = "none";

    this.form.appendChild(this.successMsg);
  }

  showLoading() {
    if (this.submitBtn) {
      this.submitBtn.classList.add("loading");
      this.submitBtn.disabled = true;
    }
  }

  hideLoading() {
    if (this.submitBtn) {
      this.submitBtn.classList.remove("loading");
      this.submitBtn.disabled = false;
    }
  }

  showSuccess() {
    this.successMsg.style.display = "block";

    setTimeout(() => {
      this.successMsg.style.display = "none";
      this.closePopup();
    }, 5000);
  }

  /* ======================================================
     SUBMIT
  ====================================================== */
  handleSubmit(e) {
    e.preventDefault();

    const name = this.querySelector("#fullName");
    const phone = this.querySelector("#phoneNumber");
    const country = this.querySelector("#countryCode");
    const citySelect = this.querySelector("#preferredCity");
    const clinicSelect = this.querySelector("#preferredClinic");

    const nameError = this.querySelector('[data-error="fullName"]');
    const phoneError = this.querySelector('[data-error="phoneNumber"]');

    // Clear errors
    nameError.textContent = "";
    phoneError.textContent = "";

    const nameVal = name.value.trim();
    const phoneVal = phone.value.trim();
    const cityVal = citySelect ? citySelect.value.trim() : "";
    const clinicVal = clinicSelect ? clinicSelect.value.trim() : "";

    // Validation (only name and phone are required)
    let hasError = false;

    if (!/^[A-Za-z ]{3,}$/.test(nameVal)) {
      nameError.textContent = "Enter a valid full name";
      hasError = true;
    }

    if (!/^[6-9][0-9]{9}$/.test(phoneVal)) {
      phoneError.textContent = "Enter a valid Indian mobile number";
      hasError = true;
    }

    if (hasError) return;

    /* -------- SHOW LOADING -------- */
    this.showLoading();

    /* -------- GET CLINIC PHONES FROM DATA ATTRIBUTE -------- */
    const clinicPhones = this.getAttribute("data-clinic-phones") || "";

    /* -------- FIRE & FORGET -------- */
    const formData = new FormData();
    formData.append("fullName", nameVal);
    formData.append("phone", phoneVal);
    formData.append("countryCode", country.value);
    formData.append("location", cityVal || "Not specified");
    formData.append("clinic", clinicVal || "Not specified");
    formData.append("clinicPhones", clinicPhones);

    fetch(this.GOOGLE_SHEET_URL, {
      method: "POST",
      body: formData,
      keepalive: true,
    }).catch(() => {
      // silent fail
    });

    /* -------- OPTIMISTIC SUCCESS -------- */
    setTimeout(() => {
      this.hideLoading();

      this.form.reset();

      // Reset to first available city and load its clinics
      const citySelect = this.querySelector("#preferredCity");
      const clinicSelect = this.querySelector("#preferredClinic");
      
      if (citySelect && clinicSelect) {
        const firstCityOption = citySelect.querySelector('option[value]:not([value=""])');
        const defaultCity = firstCityOption ? firstCityOption.value : "";
        
        if (defaultCity) {
          citySelect.value = defaultCity;
          this.selectedCity = defaultCity;
          this.updateClinicOptions(clinicSelect);
        }
      }

      this.showSuccess();
    }, 900);
  }
}

/* ======================================================
   REGISTER ELEMENT
====================================================== */
if (!customElements.get("custom-doctor-consultation-form")) {
  customElements.define(
    "custom-doctor-consultation-form",
    DoctorConsultationForm,
  );
}
